import { PRODUCTION_TENANT_TYPES, TenantTypes } from '@sit/core';
import { useGetCurrentCompany } from '@web/api/company/use-get-company';
import { useIsAuthenticated } from '@web/auth/use-is-authenticated';
import { BuildType, setBuildType, useIsStandalone } from '@web/intacct/build-type';
import omit from 'lodash/omit';
import { useEffect } from 'react';
import { useLocation, useParams } from 'react-router';
import { handleParamsValidation } from '../helpers';

type PrivatePageStates = 'UNAUTHENTICATED' | 'INVALID_PARAMS' | 'AUTHORIZED';

interface UsePrivateDataProps {
  paramsToValidate?: Record<string, string>;
}

const NON_PRODUCTION_TENANTS = Object.values(omit(TenantTypes, PRODUCTION_TENANT_TYPES));

export const usePrivateData = ({ paramsToValidate }: UsePrivateDataProps): PrivatePageStates => {
  const { data: company } = useGetCurrentCompany();
  const isAuthenticated = useIsAuthenticated();
  const isStandalone = useIsStandalone();
  const params = useParams();
  const location = useLocation();
  const shouldValidateParams = paramsToValidate && Object.keys(paramsToValidate).length > 0;
  const paramsAreValid = shouldValidateParams && handleParamsValidation(paramsToValidate, params);
  const { search } = location;

  const searchParams = new URLSearchParams(search);
  const queryForceEmbedded = searchParams.get('force_embedded_mode') === 'true';

  const isNonProductionTenant = company?.tenantType && (NON_PRODUCTION_TENANTS as string[]).includes(company.tenantType);

  useEffect(() => {
    if (isStandalone && queryForceEmbedded && isNonProductionTenant) {
      setBuildType(BuildType.embedded);
    }
  }, [isStandalone, queryForceEmbedded, isNonProductionTenant]);

  if (!isAuthenticated) {
    return 'UNAUTHENTICATED';
  }

  if (shouldValidateParams && !paramsAreValid) {
    return 'INVALID_PARAMS';
  }

  return 'AUTHORIZED';
};
